import { formatEUR } from '../utils/money';

/** @typedef {'guaranteed' | 'variable' | 'occasional'} IncomeSourceBucketId */
/** @typedef {'monthly_fixed' | 'monthly_variable' | 'occasional'} IncomeSourceRecurrence */

/**
 * Baldes de fiabilidade. Cores = `.pen` `ds/card/composition-group` ×
 * `Fontes de Rendimento · Normal` (chip do cabeçalho de grupo + barra de
 * repartição do cartão resumo): Garantido → sage, Variável → info,
 * Pontual → warning.
 */
export const RECURRENCE_BUCKETS = [
  {
    id: 'guaranteed',
    recurrence: 'monthly_fixed',
    labelKey: 'guaranteed',
    tone: {
      chipBg: 'bg-sage-tint',
      chipFg: 'text-sage-on-tint',
      dot: 'bg-sage-500',
    },
  },
  {
    id: 'variable',
    recurrence: 'monthly_variable',
    labelKey: 'variable',
    tone: {
      chipBg: 'bg-info-tint',
      chipFg: 'text-info-fg',
      dot: 'bg-info-500',
    },
  },
  {
    id: 'occasional',
    recurrence: 'occasional',
    labelKey: 'occasional',
    tone: {
      chipBg: 'bg-warning-tint',
      chipFg: 'text-warning-fg',
      dot: 'bg-warning-500',
    },
  },
];

const RECURRENCE_TO_BUCKET = Object.fromEntries(
  RECURRENCE_BUCKETS.map((b) => [b.recurrence, b]),
);

const BUCKET_BY_ID = Object.fromEntries(RECURRENCE_BUCKETS.map((b) => [b.id, b]));

/**
 * @param {string | null | undefined} recurrence
 * @returns {typeof RECURRENCE_BUCKETS[number]}
 */
export function bucketForRecurrence(recurrence) {
  const key = String(recurrence || '').toLowerCase();
  return RECURRENCE_TO_BUCKET[key] || RECURRENCE_BUCKETS[0];
}

/**
 * @param {IncomeSourceBucketId} bucketId
 */
export function bucketById(bucketId) {
  return BUCKET_BY_ID[bucketId] || RECURRENCE_BUCKETS[0];
}

/**
 * @param {Array<{ display_order?: number; name?: string }>} sources
 */
export function sortSources(sources = []) {
  return [...sources].sort((a, b) => {
    const orderA = a.display_order ?? 0;
    const orderB = b.display_order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || '').localeCompare(b.name || '', 'pt');
  });
}

/**
 * @param {Array<{ recurrence?: string; is_active?: boolean }>} sources
 * @returns {Record<IncomeSourceBucketId, Array>}
 */
export function groupSourcesByBucket(sources = []) {
  const groups = {
    guaranteed: [],
    variable: [],
    occasional: [],
  };
  sortSources(sources.filter((s) => s.is_active !== false)).forEach((source) => {
    const bucket = bucketForRecurrence(source.recurrence);
    groups[bucket.id].push(source);
  });
  return groups;
}

/**
 * @param {Array<{ base_amount?: string|number|null }>} sources
 * @returns {number}
 */
export function sumBucketAmount(sources = []) {
  return sources.reduce((sum, source) => {
    const amount = Number(source.base_amount);
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);
}

/**
 * Display do valor típico no chip (fixo / ~típico / —).
 * @param {{ base_amount?: string|number|null; recurrence?: string }} source
 * @returns {{ kind: 'exact'|'approx'|'none'; label: string; amount: number|null }}
 */
export function formatSourceTypicalAmount(source) {
  const raw = source?.base_amount;
  if (raw === null || raw === undefined || raw === '') {
    return { kind: 'none', label: '—', amount: null };
  }
  const amount = Number(raw);
  if (!Number.isFinite(amount)) {
    return { kind: 'none', label: '—', amount: null };
  }
  const formatted = formatEUR(amount);
  const bucket = bucketForRecurrence(source.recurrence);
  if (bucket.id === 'guaranteed') {
    return { kind: 'exact', label: formatted, amount };
  }
  if (bucket.id === 'variable') {
    return { kind: 'approx', label: `~${formatted}`, amount };
  }
  return { kind: 'approx', label: `~${formatted}`, amount };
}
